import { useEffect, useRef, useState } from "react";

import type { AssetEvent, AssetListItem } from "@/lib/api";
import { api } from "@/lib/api";
import { formatDateTimeSec } from "@/lib/format";
import { detalheDaTrocaNoHistorico } from "@/lib/viewerKindOverride";

interface AssetHistoryProps {
  asset: AssetListItem;
  onClose: () => void;
}

const EVENT_META: Record<
  string,
  { label: string; icon: string; colorClass: string }
> = {
  uploaded: {
    label: "Upload realizado",
    icon: "↑",
    colorClass: "bg-blue-900/60 text-blue-300 ring-blue-700/50",
  },
  render_200_started: {
    label: "Render 200 DPI — iniciado",
    icon: "▶",
    colorClass: "bg-amber-900/60 text-amber-300 ring-amber-700/50",
  },
  render_started: {
    label: "Render — iniciado",
    icon: "▶",
    colorClass: "bg-amber-900/60 text-amber-300 ring-amber-700/50",
  },
  render_200_finished: {
    label: "Render 200 DPI — concluído",
    icon: "✓",
    colorClass: "bg-emerald-900/60 text-emerald-300 ring-emerald-700/50",
  },
  render_finished: {
    label: "Render — concluído",
    icon: "✓",
    colorClass: "bg-emerald-900/60 text-emerald-300 ring-emerald-700/50",
  },
  render_timeout: {
    label: "Render — timeout",
    icon: "⏱",
    colorClass: "bg-red-900/60 text-red-300 ring-red-700/50",
  },
  render_failed: {
    label: "Render — falhou",
    icon: "!",
    colorClass: "bg-red-900/60 text-red-300 ring-red-700/50",
  },
  preprepare_finished: {
    label: "Análise do arquivo — concluída",
    icon: "✓",
    colorClass: "bg-emerald-900/60 text-emerald-300 ring-emerald-700/50",
  },
  preprepare_failed: {
    label: "Análise do arquivo — falhou",
    icon: "!",
    colorClass: "bg-red-900/60 text-red-300 ring-red-700/50",
  },
  viewer_opened: {
    label: "Viewer aberto",
    icon: "👁",
    colorClass: "bg-ink-700 text-ink-200 ring-ink-600",
  },
  public_view_opened: {
    label: "Link público aberto",
    icon: "👁",
    colorClass: "bg-ink-700 text-ink-200 ring-ink-600",
  },
  // F-S5 (T-62): os dois eventos que tornam um vazamento investigável — quem
  // levou o original, e quem ainda abre o link sem se identificar.
  downloaded: {
    label: "Original baixado",
    icon: "↓",
    colorClass: "bg-blue-900/60 text-blue-300 ring-blue-700/50",
  },
  anonymous_slug_access: {
    label: "Aberto por link, sem sessão",
    icon: "👁",
    colorClass: "bg-amber-900/60 text-amber-300 ring-amber-700/50",
  },
  // O «Tipo de viewer…» do gerenciador (29/09/2026): de/para vão no metadata.
  viewer_kind_changed: {
    label: "Tipo de viewer trocado",
    icon: "✎",
    colorClass: "bg-sky-900/60 text-sky-300 ring-sky-700/50",
  },
  // A preparação da rota nova falhou e o servidor devolveu o arquivo ao que
  // era; o sufixo `_failed` traz o motivo (`failureDetail`).
  viewer_kind_change_failed: {
    label: "Troca de viewer desfeita",
    icon: "↺",
    colorClass: "bg-amber-900/60 text-amber-300 ring-amber-700/50",
  },
  // F-GC.4: o GC tirou os bytes (pirâmide e original) e o reenvio os trouxe de
  // volta; o metadata diz quanto saiu e quando tinha expirado.
  expired: {
    label: "Expirado no viewer",
    icon: "⌛",
    colorClass: "bg-yellow-900/60 text-yellow-300 ring-yellow-700/50",
  },
  revived: {
    label: "Reenviado e reativado",
    icon: "↻",
    colorClass: "bg-emerald-900/60 text-emerald-300 ring-emerald-700/50",
  },
};

/**
 * O motivo da recusa, como o worker o gravou.
 *
 * O evento de falha já carrega tudo — `error` sempre, e `code`, `reason`,
 * `terminal` e `rflow_version` quando o erro do daemon os tipa (ver
 * `_technical_failure` no worker) —, e o Histórico simplesmente não olhava
 * para nada disso. Uma arte recusada pela política de pixels aparecia aqui
 * como `preprepare_failed` cru, sem uma palavra sobre o porquê, e o texto só
 * existia no log do contêiner, que rotaciona.
 *
 * Este painel é de administrador (o endpoint `/assets/{id}/history` exige
 * isso), como a Fila que já mostra o `error` do job. A regra da F9.4 — nenhum
 * texto do servidor no véu do **viewer** — continua valendo lá, onde ela foi
 * escrita: quem abre um link compartilhado não vê nada disto.
 */
function failureDetail(
  metadata: Record<string, unknown> | null,
): { message: string | null; tags: string[] } {
  if (!metadata) return { message: null, tags: [] };
  const message =
    typeof metadata["error"] === "string" && metadata["error"].trim()
      ? metadata["error"].trim()
      : null;
  const tags: string[] = [];
  for (const field of ["code", "reason", "terminal", "phase", "rflow_version"]) {
    const value = metadata[field];
    if (typeof value === "string" && value.trim()) tags.push(`${field}: ${value.trim()}`);
  }
  return { message, tags };
}

function renderModeLabel(mode: string | null | undefined): string | null {
  if (mode === "channels") return "Com Canais";
  if (mode === "composite") return "Sem Canais";
  return null;
}

export function AssetHistory({ asset, onClose }: AssetHistoryProps) {
  const [events, setEvents] = useState<AssetEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getAssetHistory(asset.id)
      .then((data) => {
        if (!cancelled) setEvents(data);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [asset.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (event.target === overlayRef.current) onClose();
      }}
    >
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-ink-700 bg-ink-900 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-ink-700 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink-100">Histórico</h2>
            <p
              className="truncate text-[11px] text-ink-400"
              title={asset.filename}
            >
              {asset.filename}
            </p>
          </div>
          <button
            type="button"
            aria-label="Fechar histórico"
            onClick={onClose}
            className="shrink-0 rounded p-1 text-ink-400 hover:bg-ink-700 hover:text-ink-100"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 scrollbar-thin">
          {loading && (
            <p className="text-sm text-ink-400">Carregando histórico…</p>
          )}
          {error && (
            <p className="text-sm text-red-300">Erro ao carregar: {error}</p>
          )}
          {!loading && !error && events.length === 0 && (
            <p className="text-sm text-ink-500">
              Nenhum evento registrado ainda. Eventos são gerados a partir do
              próximo upload ou render.
            </p>
          )}
          {!loading && !error && events.length > 0 && (
            <ol className="relative border-l border-ink-700 pl-5">
              {events.map((event, index) => {
                const meta =
                  EVENT_META[event.event_type] ?? {
                    label: event.event_type,
                    icon: "•",
                    colorClass: "bg-ink-700 text-ink-200 ring-ink-600",
                  };
                const mode =
                  event.metadata_json &&
                  typeof event.metadata_json["mode"] === "string"
                    ? renderModeLabel(event.metadata_json["mode"])
                    : null;
                const dpi =
                  event.metadata_json &&
                  typeof event.metadata_json["dpi"] === "number"
                    ? (event.metadata_json["dpi"] as number)
                    : null;
                const falha = event.event_type.endsWith("_failed")
                  ? failureDetail(event.metadata_json)
                  : null;
                const troca = detalheDaTrocaNoHistorico(event.event_type, event.metadata_json);

                return (
                  <li key={event.id} className={`mb-4 ${index === events.length - 1 ? "" : ""}`}>
                    {/* Timeline dot */}
                    <span
                      className={`absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ring-1 ${meta.colorClass}`}
                    >
                      {meta.icon}
                    </span>

                    <div className="ml-1">
                      <p className="text-xs font-medium text-ink-100">
                        {meta.label}
                      </p>
                      <p className="mt-0.5 text-[11px] text-ink-400">
                        {formatDateTimeSec(event.occurred_at)}
                      </p>
                      {troca && <p className="mt-0.5 text-[11px] text-sky-300">{troca}</p>}
                      {(mode || dpi) && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {dpi && (
                            <span className="rounded bg-ink-800 px-1.5 py-0.5 text-[10px] text-ink-300">
                              {dpi} DPI
                            </span>
                          )}
                          {mode && (
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] ring-1 ${
                                mode === "Com Canais"
                                  ? "bg-violet-900/60 text-violet-300 ring-violet-700/50"
                                  : "bg-amber-900/60 text-amber-300 ring-amber-700/50"
                              }`}
                            >
                              {mode}
                            </span>
                          )}
                        </div>
                      )}
                      {falha?.message && (
                        <p className="mt-1.5 whitespace-pre-wrap break-words rounded border border-red-900/60 bg-red-950/40 px-2 py-1.5 text-[11px] leading-relaxed text-red-200">
                          {falha.message}
                        </p>
                      )}
                      {falha && falha.tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {falha.tags.map((tag) => (
                            <span
                              key={tag}
                              className="rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[10px] text-ink-300"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
